import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { MegaMenuItemLink, NavMenuControlButton } from 'components/Layout/Header/components';
import MegaMenuItem from 'components/Layout/Header/components/MegaMenuItem/MegaMenuItem';

import { useFocusTracking } from 'hooks/useFocusTracking';

import { SubMenuItemI } from 'interfaces/content-stack/contentStack.types';

import { pushDataLayerEvent } from 'utils/dataLayerUtils';
import { getModularBlockLink } from 'utils/modularBlockUtils';

interface MegaMenuProps {
  items: SubMenuItemI[];
}

const MenuItems = styled.nav`
  display: flex;
  align-items: center;
  gap: 2.5rem;
`;

const handleMainNavItemClick = (linkText: string, url: string) => {
  pushDataLayerEvent({
    event: 'nav_bar',
    event_category: 'nav bar',
    event_action: 'navigate',
    event_label: linkText,
    link_to: url,
  });
};

const MegaMenuItems: React.FC<MegaMenuProps> = ({ items }) => {
  const [openedMenuIndex, setOpenedMenuIndex] = useState<number | null>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);
  const { isInFocus: isNavInFocus } = useFocusTracking(menuItemsRef, {
    isIgnoreMouseEvents: true,
    isIgnoreTouchEvents: true,
  });

  const closeMenu = () => {
    setOpenedMenuIndex(null);
  };

  const openMenu = (menuIndex: number) => {
    setOpenedMenuIndex(menuIndex);
  };

  useEffect(() => {
    if (!isNavInFocus) {
      closeMenu();
    }
  }, [isNavInFocus]);

  return (
    <MenuItems ref={menuItemsRef} data-testid='menu-items-test-id'>
      {items?.map(({ linkText, link, menuColumns }, i: number) => {
        const url = getModularBlockLink(link);
        const isMenuOpen = openedMenuIndex === i;
        const itemRef = useRef<HTMLDivElement>(null);
        const itemMenuControlButtonRef = useRef<HTMLButtonElement>(null);

        const mouseLeaveHandler = () => {
          closeMenu();
        };

        const linkMouseEnterHandler = () => {
          openMenu(i);
        };

        const toggleMenu = () => {
          if (isMenuOpen) {
            closeMenu();
          } else {
            openMenu(i);
          }
        };

        const navMenuControlButtonKeyDownHandler = (event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (['Enter', 'Space'].includes(event.code)) {
            toggleMenu();
          }
        };

        const menuItemKeydownHandler = (event: React.KeyboardEvent) => {
          if (event.code === 'Escape') {
            itemMenuControlButtonRef.current?.focus();
            closeMenu();
          }
        };

        const menuItemLinkClickHandler = () => handleMainNavItemClick(linkText ?? '', url ?? '/');

        const menuItemFocusHandler = () => {
          closeMenu();
        };

        return (
          <MegaMenuItem
            onMouseLeave={mouseLeaveHandler}
            onKeyDown={menuItemKeydownHandler}
            data={menuColumns}
            key={link?.[0]?.id ?? i}
            index={i}
            isMenuOpen={isMenuOpen}
            ref={itemRef}
          >
            <div onFocus={menuItemFocusHandler}>
              <MegaMenuItemLink onMouseEnter={linkMouseEnterHandler} onClick={menuItemLinkClickHandler} to={url ?? '/'}>
                {linkText}
              </MegaMenuItemLink>
              {isNavInFocus && (
                <NavMenuControlButton
                  onKeyDown={navMenuControlButtonKeyDownHandler}
                  isExpanded={isMenuOpen}
                  ref={itemMenuControlButtonRef}
                />
              )}
            </div>
          </MegaMenuItem>
        );
      })}
    </MenuItems>
  );
};

export default MegaMenuItems;
